<!--
Public: Allows to embed inline svg, making a request only once it's used, and
caching it aggressively. As a result, it's very efficient.

NOTE: Available icons are defined in @corp/icons/aliases.ts
-->
<script lang="ts">
import type { IconDefinition as FontAwesomeIconDefinition } from '@fortawesome/fontawesome-common-types'
export type IconSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'inherit'
export type IconDefinition = string | FontAwesomeIconDefinition
</script>

<script setup lang="ts">import { computed as _computed } from 'vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

import { svgForIcon } from '@corp/services/icons'
import { toIconName } from '@corp/icons/aliases'

interface Props {
  // Deprecated: Used to retrieve an svg url from the icon aliases.
  name?: string

  // A font awesome icon definition to render instead of an svg.
  icon?: IconDefinition

  // The size to use for the icon.
  size?: IconSize
}

withDefaults(defineProps<Props>(), { name: '',size: 'sm', })

const explicitName = _computed(() => __props.name || (typeof __props.icon === 'string' ? __props.icon : undefined))

const classes = _computed(() => [explicitName.value && `icon-${toIconName(explicitName.value)}`, __props.size])

const svgIcon = _computed(() => {
  if (!explicitName.value) return undefined

  try {
    return svgForIcon(explicitName.value).value
  }
  catch (error: any) {
    const instance = getCurrentInstance()
    console.error(error.message, instance?.parent?.vnode)
  }
})
</script>

<template>
  <i
    v-if="explicitName"
    class="icon"
    :class="classes"
    v-html="svgIcon"
  />
  <FontAwesomeIcon
    v-else
    :icon="icon as FontAwesomeIconDefinition"
    :class="['icon', size]"
  />
</template>

<style lang="scss" scoped>
@use "sass:math";

$ratio: math.div(1rem, 4);

.icon {
  box-sizing: content-box;
  display: inline-block;
  flex-shrink: 0;
  height: var(--size);
  width: var(--size);
}

.inherit {
  height: inherit;
  width: inherit;
}

.xs {
  --size: #{3 * $ratio};
}

.sm {
  --size: #{4 * $ratio};
}

.md {
  --size: #{5.5 * $ratio};
}

.lg {
  --size: #{6 * $ratio};
}

.xl {
  --size: #{6.5 * $ratio};
}

.icon > :deep(svg) {
  fill: currentcolor;
  height: inherit;
  width: inherit;
}

</style>
